import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@mbbe/ui';
import { LogOut } from 'lucide-react';
import { useDestekAuth } from './DestekAuthContext';
import { DestekHelp } from './components/DestekHelp';
import { NotificationBell } from './components/NotificationBell';
import type { PageHelpKey } from './lib/helpContent';
import { useLanguage } from '../contexts/LanguageContext';
import { updateCustomerLanguage } from '../portal/lib/customerApi';
import { useSupportSurface } from './SupportSurfaceContext';
import { portalMemberName, portalOrganizationName } from '../portal/lib/portalAccess';

export function DestekLayout({ children, helpKey }: { children: ReactNode; helpKey?: PageHelpKey }) {
  const { me, signOut } = useDestekAuth();
  const { language, setLanguage, t } = useLanguage();
  const name = portalMemberName(me);
  const organizationName = portalOrganizationName(me);
  const { isPortal } = useSupportSurface();

  const toggleLanguage = async () => {
    const nextLang = language === 'tr' ? 'en' : 'tr';
    setLanguage(nextLang);
    try {
      await updateCustomerLanguage(nextLang);
    } catch (error) {
      console.error('Failed to update language on backend:', error);
      setLanguage(language);
    }
  };

  if (isPortal) {
    return (
      <section className="relative min-w-0">
        {helpKey && (
          <div className="mb-4 flex justify-end">
            <DestekHelp pageKey={helpKey} />
          </div>
        )}
        {children}
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 text-white relative overflow-x-hidden">
      {/* Atmospheric brand glow */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(60rem_40rem_at_80%_-10%,rgba(59,130,246,0.10),transparent_60%),radial-gradient(50rem_30rem_at_-10%_10%,rgba(37,99,235,0.08),transparent_55%)]"
      />

      <header className="sticky top-0 z-30 border-b border-white/5 bg-dark-900/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <Logo variant="contained" className="h-8 w-8" />
            <span className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold tracking-tight">mbbe</span>
              <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-brand-300">
                {t('destek_support')}
              </span>
            </span>
          </Link>

          <div className="flex items-center gap-3">
            {name && (
              <span className="hidden max-w-[220px] text-right sm:block">
                <span className="block truncate text-sm text-slate-300">{name}</span>
                {organizationName && organizationName !== name && <span className="block truncate text-[11px] text-slate-500">{organizationName}</span>}
              </span>
            )}
            <button
              type="button"
              onClick={toggleLanguage}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-white/5 bg-white/[0.03] px-2 text-xs font-semibold text-slate-400 hover:border-brand-400/30 hover:text-white transition-colors focus:outline-none"
            >
              {language === 'tr' ? 'EN' : 'TR'}
            </button>
            <NotificationBell />
            {helpKey && <DestekHelp pageKey={helpKey} />}
            <button
              onClick={signOut}
              aria-label={t('destek_logout')}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-md px-3 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">{t('destek_logout')}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="relative max-w-4xl mx-auto px-4 py-8">{children}</main>
    </div>
  );
}
